/**
 * F09 v2 — read-only panels for the admin request detail + entity pages:
 * evaluator signals (by entity type) + risk flags, the events timeline
 * (`AuditLogTimeline`), the request-history table and the name-change diff.
 */
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useFormat, useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  Badge,
  Card,
  type AuditEvent,
  type AuditEventKind,
} from '@bravemove/ui-web';
import {
  isCappingRiskFlag,
  type VerificationEvaluatorSignals,
  type VerificationEventEntry,
  type VerificationEventKind,
  type VerificationRequestHistoryItem,
  type VerificationRiskFlag,
} from '@bravemove/types';
import {
  eventLabel,
  kindLabel,
  rejectionLabel,
  requestPath,
  revocationLabel,
  riskFlagLabel,
  signalLabel,
  signalValue,
} from './labels';
import { RequestStatusBadge } from './VerificationBits';

export function Panel({
  title,
  children,
  testId,
  action,
}: {
  readonly title: ReactNode;
  readonly children: ReactNode;
  readonly testId?: string;
  readonly action?: ReactNode;
}) {
  return (
    <Card>
      <section className="space-y-3" data-testid={testId}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
            {title}
          </h2>
          {action}
        </div>
        {children}
      </section>
    </Card>
  );
}

export function RiskFlagList({ flags }: { readonly flags: readonly VerificationRiskFlag[] }) {
  const { t } = useI18n();
  if (flags.length === 0) {
    return <p className="text-[13px] text-ink-3">{t('admin:verification.panels.noRiskFlags')}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((f) => (
        <Badge key={f} tone={isCappingRiskFlag(f) ? 'danger' : 'warning'} size="sm">
          {riskFlagLabel(t, f)}
        </Badge>
      ))}
    </div>
  );
}

export function SignalsPanel({
  signals,
  riskFlags,
}: {
  readonly signals: VerificationEvaluatorSignals | null;
  readonly riskFlags: readonly VerificationRiskFlag[];
}) {
  const { t } = useI18n();
  const entries = signals
    ? Object.entries(signals).filter(([k]) => k !== 'entityType' && k !== 'risk')
    : [];
  return (
    <Panel title={t('admin:verification.panels.signals')} testId="panel-signals">
      {entries.length === 0 ? (
        <p className="text-[13px] text-ink-3">{t('admin:verification.signals.empty')}</p>
      ) : (
        <dl
          className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-[13px]"
          data-entity-type={signals?.entityType}
        >
          {entries.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-ink-3">{signalLabel(t, key)}</dt>
              <dd className="text-end font-semibold text-ink tabular-nums">
                {signalValue(t, value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className="space-y-1.5">
        <h3 className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.panels.riskFlags')}
        </h3>
        <RiskFlagList flags={riskFlags} />
      </div>
    </Panel>
  );
}

const EVENT_TONE: Partial<Record<VerificationEventKind, AuditEventKind>> = {
  request_created: 'create',
  approved: 'create',
  direct_verified: 'create',
  name_change_applied: 'update',
  rejected: 'delete',
  revoked: 'delete',
  withdrawn: 'delete',
  superseded: 'info',
  duplicate_identity_flagged: 'alert',
  watchlist_flagged: 'alert',
  name_lock_blocked: 'permission',
  name_lock_override: 'permission',
  cooldown_lifted: 'permission',
  rerequest_unlocked: 'permission',
};

export function EventsTimeline({ events }: { readonly events: readonly VerificationEventEntry[] }) {
  const { t } = useI18n();
  const { formatDateTime } = useFormat();
  if (events.length === 0) {
    return <p className="text-[13px] text-ink-3">{t('admin:verification.panels.noEvents')}</p>;
  }
  const items: AuditEvent[] = [...events]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((e) => ({
      id: e.id,
      at: formatDateTime(new Date(e.createdAt)),
      kind: EVENT_TONE[e.eventKind] ?? 'info',
      summary: eventLabel(t, e.eventKind),
      actor: {
        name:
          e.actorDisplayName ??
          (e.actorKind === 'system'
            ? t('admin:verification.event.actorSystem')
            : t('admin:verification.event.actorUnknown')),
      },
      ...(e.reason ? { details: e.reason } : {}),
    }));
  return <AuditLogTimeline events={items} />;
}

export function RequestHistoryTable({
  rows,
  currentId,
}: {
  readonly rows: readonly VerificationRequestHistoryItem[];
  readonly currentId?: string;
}) {
  const { t } = useI18n();
  const { formatDate } = useFormat();
  const fmt = (iso: string | null) =>
    iso ? formatDate(new Date(iso)) : t('admin:verification.common.none');
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-start text-[11px] uppercase tracking-wider text-ink-3">
            <th className="py-1 pe-3 text-start">
              {t('admin:verification.entity.columns.request')}
            </th>
            <th className="py-1 pe-3 text-start">{t('admin:verification.entity.columns.kind')}</th>
            <th className="py-1 pe-3 text-start">
              {t('admin:verification.entity.columns.status')}
            </th>
            <th className="py-1 pe-3 text-start">
              {t('admin:verification.entity.columns.submitted')}
            </th>
            <th className="py-1 text-start">{t('admin:verification.entity.columns.decided')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-hairline" data-testid={`history-${r.id}`}>
              <td className="py-1.5 pe-3 font-mono text-[11px]">
                {r.id === currentId ? (
                  r.id.slice(0, 8)
                ) : (
                  <Link className="text-accent hover:underline" to={requestPath(r.id)}>
                    {r.id.slice(0, 8)}
                  </Link>
                )}
              </td>
              <td className="py-1.5 pe-3">{kindLabel(t, r.requestKind)}</td>
              <td className="py-1.5 pe-3">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <RequestStatusBadge status={r.status} />
                  {r.decisionReasonCategory ? (
                    <span className="text-[12px] text-ink-3">
                      {rejectionLabel(t, r.decisionReasonCategory)}
                    </span>
                  ) : null}
                  {r.revocationReasonCategory ? (
                    <span className="text-[12px] text-ink-3">
                      {revocationLabel(t, r.revocationReasonCategory)}
                    </span>
                  ) : null}
                </span>
              </td>
              <td className="py-1.5 pe-3 tabular-nums">{fmt(r.submittedAt)}</td>
              <td className="py-1.5 tabular-nums">{fmt(r.decidedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NameChangeDiff({
  currentName,
  requestedName,
  currentLegalName,
  requestedLegalName,
}: {
  readonly currentName: string;
  readonly requestedName: string | null;
  readonly currentLegalName: string | null;
  readonly requestedLegalName: string | null;
}) {
  const { t } = useI18n();
  const row = (label: string, from: string | null, to: string | null, testId: string) => (
    <div
      className="grid gap-1 sm:grid-cols-[10rem_1fr_auto_1fr] sm:items-center"
      data-testid={testId}
    >
      <span className="text-[12px] text-ink-3">{label}</span>
      <span className="rounded-md bg-danger/10 px-2 py-1 text-danger line-through decoration-danger/60">
        {from ?? t('admin:verification.common.none')}
      </span>
      <span aria-hidden="true" className="hidden text-ink-3 sm:inline">
        →
      </span>
      <span className="rounded-md bg-success/10 px-2 py-1 font-semibold text-success">
        {to ?? t('admin:verification.common.none')}
      </span>
    </div>
  );
  return (
    <Panel title={t('admin:verification.panels.nameChange')} testId="panel-name-change">
      <div className="space-y-2 text-[13px]">
        {row(
          t('admin:verification.panels.currentName'),
          currentName,
          requestedName,
          'name-diff-primary',
        )}
        {requestedLegalName !== null
          ? row(
              t('admin:verification.panels.currentLegalName'),
              currentLegalName,
              requestedLegalName,
              'name-diff-legal',
            )
          : null}
      </div>
    </Panel>
  );
}
